/**
 * Semantic + engine validation over a {@link QueryPlan}.
 *
 * Shared semantic rules run for every engine. Engine-specific rules are added
 * via {@link PlanValidationRule} — the extension point for "known unsupported
 * combinations" (e.g. an engine that lacks a date granularity or an
 * aggregation), instead of scattering `if (engine === …)`.
 */
import type { Engine } from '../input/input-types';
import { QueryBuilderError } from '../errors/query-builder-error';
import type { QueryPlan } from './query-plan';

export const MAX_LIMIT = 10_000;
export const MAX_SKIP = 10_000;
export const MAX_SELECTED_FIELDS = 50;

export type PlanValidationContext = {
    engine: Engine;
};

/** A composable validation rule. Return errors (empty = valid). */
export interface PlanValidationRule {
    validate(
        plan: QueryPlan,
        context: PlanValidationContext,
    ): QueryBuilderError[];
}

/**
 * Engine-specific validation rules for known unsupported combinations. Register
 * a rule here to reject a combination for a given engine.
 *
 * Example:
 *   engineRules.push({
 *     validate(plan, ctx) {
 *       if (ctx.engine === 'clickhouse' && usesUnsupportedThing(plan)) {
 *         return [new QueryBuilderError({ code: 'UNSUPPORTED_ENGINE_BEHAVIOR', ... })];
 *       }
 *       return [];
 *     },
 *   });
 */
export const engineRules: PlanValidationRule[] = [];

export function validatePlan(
    plan: QueryPlan,
    context: PlanValidationContext,
): QueryBuilderError[] {
    const errors: QueryBuilderError[] = [];

    const hasSelect = plan.projection !== null;
    const hasGrouping = plan.grouping !== null;
    const hasAggregations = plan.aggregations.length > 0;

    // Output shape combinations.
    if (!hasSelect && !hasGrouping && !hasAggregations) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_INPUT',
                path: '$',
                message: 'Query must define select, groupBy, or aggregations.',
            }),
        );
    }
    if (hasSelect && hasGrouping) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_GROUP_SELECTION',
                path: '$.select',
                message:
                    'select cannot be provided when groupBy exists. Grouped output is inferred from groupBy and aggregations.',
            }),
        );
    }
    if (hasSelect && hasAggregations && !hasGrouping) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_AGGREGATION_SELECTION',
                path: '$.select',
                message:
                    'select cannot be combined with aggregations unless groupBy rules are satisfied.',
            }),
        );
    }

    // Limits.
    if (plan.limits.limit > MAX_LIMIT) {
        errors.push(
            new QueryBuilderError({
                code: 'LIMIT_EXCEEDED',
                path: '$.limit',
                message: `limit must be <= ${MAX_LIMIT}.`,
            }),
        );
    }
    if (plan.pagination.skip !== null && plan.pagination.skip > MAX_SKIP) {
        errors.push(
            new QueryBuilderError({
                code: 'SKIP_EXCEEDED',
                path: '$.skip',
                message: `skip must be <= ${MAX_SKIP}.`,
            }),
        );
    }
    if (
        plan.projection &&
        plan.projection.fields.length > MAX_SELECTED_FIELDS
    ) {
        errors.push(
            new QueryBuilderError({
                code: 'MAX_SELECTED_FIELDS_EXCEEDED',
                path: '$.select',
                message: `A maximum of ${MAX_SELECTED_FIELDS} fields may be selected.`,
            }),
        );
    }

    // Pagination.
    if (plan.pagination.skip !== null && plan.pagination.cursor !== null) {
        errors.push(
            new QueryBuilderError({
                code: 'AMBIGUOUS_PAGINATION',
                path: '$',
                message:
                    'skip and cursor cannot be provided together because pagination strategy would be ambiguous.',
            }),
        );
    }
    if (plan.pagination.cursor !== null && plan.ordering.length === 0) {
        errors.push(
            new QueryBuilderError({
                code: 'SEARCH_AFTER_REQUIRES_ORDER_BY',
                path: '$.orderBy',
                message: 'cursor pagination (search_after) requires orderBy.',
            }),
        );
    }

    // Grouped ordering must reference grouped fields or aggregations.
    if (hasGrouping) {
        const groupFields = new Set(
            plan.grouping!.groups.map((g) => g.field.field),
        );
        for (const order of plan.ordering) {
            if (
                order.target.kind === 'field' &&
                !groupFields.has(order.target.field.field)
            ) {
                errors.push(
                    new QueryBuilderError({
                        code: 'INVALID_ORDER_BY',
                        path: order.path,
                        message:
                            'Grouped queries can only order by grouped fields or aggregations.',
                    }),
                );
            }
        }
    }

    // Engine-specific rules (extension point).
    for (const rule of engineRules) {
        errors.push(...rule.validate(plan, context));
    }

    return errors;
}
